<script lang="ts">
  import { ChevronRight, ChevronDown } from 'lucide-svelte'
  import type { FileNode } from './FileExplorer.svelte'
  import FileNodeComponent from './FileNode.svelte'
  import FolderNodeComponent from './FolderNode.svelte'
  
  // Action to select text on focus
  function selectText(node: HTMLInputElement) {
    setTimeout(() => {
      node.focus()
      node.select()
    }, 10)
  }
  
  interface Props {
    node: FileNode
    depth: number
    isSelected: boolean
    isFocused: boolean
    isDragged: boolean
    isDropTarget: boolean
    selectedFiles: Set<string>
    focusedNodeId: string | null
    draggedFiles: Set<string>
    dropTargetId: string | null
    editingNodeId: string | null
    editingTitle: string
    editingDuplicateWarning?: string
    isFileOpen: (id: string) => boolean
    onToggle: () => void
    onToggleFolder: (node: FileNode) => void
    onSelect: (event: MouseEvent) => void
    onSelectFile: (node: FileNode, event: MouseEvent) => void
    onContextMenu: (event: MouseEvent) => void
    onDragStart: (event: DragEvent) => void
    onDragEnd: () => void
    onDragOver: (event: DragEvent) => void
    onDragLeave: (event?: DragEvent) => void
    onDrop: (event: DragEvent) => void
    onStartEdit: (node: FileNode) => void
    onFinishEdit: () => void
    onEditKeydown: (event: KeyboardEvent) => void
    onEditTitleChange: (value: string) => void
    onFileContextMenu: (node: FileNode, event: MouseEvent) => void
    onFileDragStart: (node: FileNode, event: DragEvent) => void
    onFileDragEnd: () => void
    onFolderDragOver: (node: FileNode, event: DragEvent) => void
    onFolderDragLeave: (node: FileNode, event?: DragEvent) => void
    onFolderDrop: (node: FileNode, event: DragEvent) => void
  }
  
  let {
    node,
    depth,
    isSelected,
    isFocused,
    isDragged,
    isDropTarget,
    selectedFiles,
    focusedNodeId,
    draggedFiles,
    dropTargetId,
    editingNodeId,
    editingTitle,
    editingDuplicateWarning = '',
    isFileOpen,
    onToggle,
    onToggleFolder,
    onSelect,
    onSelectFile,
    onContextMenu,
    onDragStart,
    onDragEnd,
    onDragOver,
    onDragLeave,
    onDrop,
    onStartEdit,
    onFinishEdit,
    onEditKeydown,
    onEditTitleChange,
    onFileContextMenu,
    onFileDragStart,
    onFileDragEnd,
    onFolderDragOver,
    onFolderDragLeave,
    onFolderDrop
  }: Props = $props()
  
</script>

<div 
  class="folder-node {isSelected ? 'selected' : ''} {isFocused ? 'focused' : ''} {isDropTarget ? 'drop-target' : ''} {isDragged ? 'dragged' : ''}"
  data-folder-id={node.id}
  data-node-id={node.id}
  role="treeitem"
  aria-selected={isSelected}
  tabindex="0"
  ondragover={(e) => {
    e.stopPropagation()
    onDragOver(e)
  }}
  ondragleave={(e) => {
    e.stopPropagation()
    onDragLeave(e)
  }}
  ondrop={(e) => {
    e.stopPropagation()
    onDrop(e)
  }}
  onkeydown={(e) => {
    // Block all keyboard events when editing this folder
    if (editingNodeId === node.id) {
      e.stopPropagation()
      e.stopImmediatePropagation?.()
    }
  }}
  onkeyup={(e) => {
    // Block keyup too when editing
    if (editingNodeId === node.id) {
      e.stopPropagation()
      e.stopImmediatePropagation?.()
    }
  }}
>
  <div class="folder-header-wrapper"
    draggable={editingNodeId !== node.id}
    role="presentation"
    ondragstart={onDragStart}
    ondragend={onDragEnd}
  >
    <button
      type="button"
      class="folder-header"
      onclick={(e) => { 
        // Don't toggle when editing
        if (editingNodeId !== node.id) {
          onSelect(e);
        }
      }}
      oncontextmenu={onContextMenu}
      onkeydown={(e) => {
        // Block all key events when editing
        if (editingNodeId === node.id) {
          e.preventDefault()
          e.stopPropagation()
          return
        }
        // Handle toggle keys when not editing
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onToggle()
        }
      }}
    >
      {#if node.expanded}
        <ChevronDown size={14} strokeWidth={2} />
      {:else}
        <ChevronRight size={14} strokeWidth={2} />
      {/if}
      {#if editingNodeId === node.id}
        <div class="edit-input-wrapper">
          <input
            value={editingTitle}
            oninput={(e) => onEditTitleChange(e.currentTarget.value)}
            onblur={onFinishEdit}
            onkeydown={(e) => {
              // Stop propagation for all keys to prevent parent handlers
              e.stopPropagation()
              e.stopImmediatePropagation?.()

              // Allow space key in input
              if (e.key === ' ') {
                // Don't prevent default for space - we want it to insert a space
                return
              }

              // Call the edit keydown handler for Enter/Escape
              onEditKeydown(e)
            }}
            onkeyup={(e) => {
              // Also stop keyup propagation to be safe
              e.stopPropagation()
              e.stopImmediatePropagation?.()
            }}
            onkeypress={(e) => {
              // Stop keypress events too
              e.stopPropagation()
              e.stopImmediatePropagation?.()
            }}
            onclick={(e) => e.stopPropagation()}
            class="edit-input {editingDuplicateWarning ? 'has-warning' : ''}"
            use:selectText
          />
        </div>
      {:else}
        <span class="node-name" data-node-id={node.id} data-unique-key={node.id} title={node.name}>{node.name}</span>
      {/if}
    </button>
    {#if editingNodeId === node.id && editingDuplicateWarning}
      <div class="duplicate-warning">{editingDuplicateWarning}</div>
    {/if}
  </div>
  
  {#if node.expanded && node.children}
    <div class="folder-children">
      <div class="vertical-line"></div>
      {#each node.children as child, childIndex (childIndex)}
        {#if child.type === 'folder'}
          <FolderNodeComponent
            node={child}
            depth={depth + 1}
            isSelected={selectedFiles.has(child.id)}
            isFocused={focusedNodeId === child.id}
            isDragged={draggedFiles.has(child.id)}
            isDropTarget={dropTargetId === child.id}
            {selectedFiles}
            {focusedNodeId}
            {draggedFiles}
            {dropTargetId}
            {editingNodeId}
            {editingTitle}
            {editingDuplicateWarning}
            {isFileOpen}
            onToggle={() => onToggleFolder(child)}
            {onToggleFolder}
            onSelect={(e) => onSelectFile(child, e)}
            {onSelectFile}
            onContextMenu={(e) => onFileContextMenu(child, e)}
            onDragStart={(e) => onFileDragStart(child, e)}
            onDragEnd={onFileDragEnd}
            onDragOver={(e) => onFolderDragOver(child, e)}
            onDragLeave={(e) => onFolderDragLeave(child, e)}
            onDrop={(e) => onFolderDrop(child, e)}
            {onStartEdit}
            {onFinishEdit}
            {onEditKeydown}
            {onEditTitleChange}
            {onFileContextMenu}
            {onFileDragStart}
            {onFileDragEnd}
            {onFolderDragOver}
            {onFolderDragLeave}
            {onFolderDrop}
          />
        {:else}
          <FileNodeComponent
            id={child.id}
            name={child.name}
            document={child.document}
            isReadError={child.isReadError}
            isImage={child.isImage}
            extension={child.extension}
            depth={depth + 1}
            isOpen={isFileOpen(child.id)}
            isSelected={selectedFiles.has(child.id)}
            isFocused={focusedNodeId === child.id}
            isDragged={draggedFiles.has(child.id)}
            onSelect={(e) => onSelectFile(child, e)}
            onContextMenu={(e) => onFileContextMenu(child, e)}
            onDragStart={(e) => onFileDragStart(child, e)}
            onDragEnd={onFileDragEnd}
            onStartEdit={() => onStartEdit(child)}
            {editingNodeId}
            {editingTitle}
            {editingDuplicateWarning}
            onFinishEdit={onFinishEdit}
            onEditKeydown={onEditKeydown}
            onEditTitleChange={onEditTitleChange}
          />
        {/if}
      {/each}
    </div>
  {/if}
</div>

<style>
  .folder-node {
    user-select: none;
  }
  
  .folder-header-wrapper {
    display: flex;
    align-items: center;
    position: relative;
    overflow: visible;
  }
  
  .folder-header {
    flex: 1;
    padding: 4px 12px;
    display: flex;
    align-items: center;
    gap: 4px;
    background: transparent;
    border: none;
    color: var(--text-secondary);
    cursor: pointer;
    text-align: left;
    position: relative;
    overflow: visible;
  }
  
  .folder-header:hover {
    background: var(--bg-tertiary);
  }
  
  .folder-children {
    padding-left: 12px;
    position: relative;
  }
  
  /* Vertical line positioned independently */
  .vertical-line {
    position: absolute;
    left: 19px;
    top: 0;
    bottom: 0;
    width: 2px;
    background: var(--border-tertiary, rgba(0, 0, 0, 0.12));
    opacity: 0.8;
    z-index: 0;
  }
  
  .folder-node.selected > .folder-header-wrapper .folder-header {
    background: rgba(156, 163, 175, 0.1);
  }
  
  .folder-node.focused > .folder-header-wrapper .folder-header {
    background: rgba(156, 163, 175, 0.08);
    box-shadow: inset 0 0 0 1px rgba(156, 163, 175, 0.2);
    border-radius: 4px;
  }
  
  /* Show dragged items with active appearance */
  .folder-node.dragged > .folder-header-wrapper .folder-header {
    background: rgba(79, 70, 229, 0.1);
    border: 1px dashed rgba(79, 70, 229, 0.5);
    opacity: 0.8;
  }
  
  .folder-node.dragged {
    position: relative;
  }
  
  /* Add a ghost effect for dragged items */
  .folder-node.dragged::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(90deg, 
      transparent, 
      rgba(79, 70, 229, 0.05), 
      transparent
    );
    pointer-events: none;
  }
  
  /* Highlight the drop target folder with its immediate children area */
  .folder-node.drop-target {
    position: relative;
  }
  
  /* Use pseudo-element for visual effect to avoid layout shift */
  .folder-node.drop-target::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(79, 70, 229, 0.06);
    border-radius: 4px;
    outline: 2px solid rgba(79, 70, 229, 0.3);
    outline-offset: -2px;
    pointer-events: none;
    z-index: 0;
  }
  
  /* Ensure content is above the background */
  .folder-node.drop-target > * {
    position: relative;
    z-index: 1;
  }
  
  .folder-node.drop-target > .folder-header-wrapper .folder-header {
    background: rgba(79, 70, 229, 0.12);
  }
  
  /* Visual indicator for header without layout shift */
  .folder-node.drop-target > .folder-header-wrapper .folder-header::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background: var(--accent-primary);
    border-radius: 2px;
  }
  
  /* Lighten the immediate children area without layout shift */
  .folder-node.drop-target > .folder-children::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    background: rgba(79, 70, 229, 0.03);
    border-radius: 2px;
    pointer-events: none;
    z-index: -1;
  }
  
  /* Make sure folder-children is positioned for pseudo-element */
  .folder-children {
    position: relative;
  }
  
  .node-name {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
    padding-right: 4px;
  }
  
  .edit-input-wrapper {
    flex: 1;
    position: relative;
    min-width: 0;
    margin-left: 4px;
    overflow: visible;
  }
  
  .edit-input {
    width: 100%;
    padding: 2px 6px;
    margin: 0 2px;
    background: var(--bg-primary);
    border: 1px solid var(--accent-primary);
    border-radius: 3px;
    color: var(--text-primary);
    font-size: var(--ui-font-size-md);
    font-weight: 400;
    outline: none;
    box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.2);
  }
  
  .edit-input.has-warning {
    border-color: var(--error, #ef4444);
    box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.2);
  }
  
  .duplicate-warning {
    position: absolute;
    top: 100%;
    left: 12px;
    margin-top: 2px;
    padding: 6px 12px;
    background: var(--error, #ef4444);
    color: white;
    font-size: 12px;
    font-weight: 500;
    border-radius: 4px;
    z-index: 10000;
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3),
                0 2px 4px rgba(0, 0, 0, 0.2);
    animation: warningFadeIn 0.2s ease-out;
    line-height: 1.4;
    white-space: nowrap;
    width: fit-content;
    max-width: calc(100vw - 48px);
  }

  @keyframes warningFadeIn {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
</style>